//import { Search } from "@mui/icons-material"
import { Autocomplete, AutocompleteChangeReason, Box, Button, Paper, TextField, Typography } from "@mui/material"
import { debounce } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { usePreferitoForSdExists, useSdAllGrouped } from "../atoms/portale"
import { ResultViewerWithTable } from "./SearchSection"
import { notPrefStarColor, prefStarColor } from "../Constants"
import { ServiceDescriptor } from "../models/ServiceDescriptor"
import { StarFill } from '../svgicons/StarFill';
import NewspaperIcon from '@mui/icons-material/Newspaper'
import OperationalNotesIcon from '@mui/icons-material/SecurityUpdateGood';
import apiService from "../services/api"
// import logConstants from "../utils/logConstants"
import { useStartApplication } from "./StartApplication"
import { NewsViewDialog } from "./NewsViewDialog"
import { News } from "../models/News"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { newsAtom } from "../atoms/news"
import excerpts from "../utils/excerpts"
import { useAree } from "../utils/hooks"
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { useEditPreferito } from "../atoms/preferito"
import { useTranslation } from "react-i18next"
import { bannerOpenState } from "../atoms/banner"
import { userSettingSelected } from "../atoms/userSetting"
import BannerSelector from "./BannerSelector"

// Qui ci mettiamo gli atom/selector che ci serviranno

type NewsType = News

export type SearchOptionType = {
    label: string,
    key: string,
    items: ServiceDescriptor[],
    news?: NewsType
    group: 'sd' | 'news'
}

const searchWidth = '100%'

type NewsViewStateType = {
    open: boolean,
    items: NewsType[],
    index: number,
}
const newsViewStateDefault: NewsViewStateType = {
    open: false,
    index: 0,
    items: [],
}

const newsLimit = 30

type SearchSectionPropsType = {
    placeholer?: string,
}
export const SearchWithNewsSection: React.FC<SearchSectionPropsType> = ({ placeholer }) => {
    const allSd = useSdAllGrouped()
    const [value, setValue] = useState<SearchOptionType | null>(null)
    const setNews = useSetRecoilState<News>(newsAtom)
    const [newsViewState, setNewsViewState] = useState(newsViewStateDefault)
    const [options, setOptions] = useState<SearchOptionType[]>([])
    const [inputValue, setInputValue] = useState('')
    const startApplication = useStartApplication()
    const { aree } = useAree()
    const { t } = useTranslation();
    const userSetting = useRecoilValue(userSettingSelected)
    const isOpen = useRecoilValue<boolean>(bannerOpenState)

    const handleAnnullaNewsView = () => setNewsViewState(newsViewStateDefault)
    useEffect(() => {
        if (inputValue === '') {
            setOptions([])
        } else if (allSd) {
            const opt = allSd.filter(x => x.label?.toLowerCase()?.indexOf(inputValue.toLowerCase()) >= 0)
                .map(x => ({ ...x, group: 'sd', key: `sd-${x.items[0].id}` } as SearchOptionType))
            apiService.searchNews(inputValue, aree, newsLimit).then((docs) => {
                // Aggiungiamo i documenti alle opzioni
                const rv = opt.concat((docs ?? []).map(d => ({ group: 'news', news: d, items: [], label: d.title, key: `news-${d.id}` } as SearchOptionType)))
                setOptions(rv)
            })
            // Intanto impostiamo le applicazioni che sono disponibili sincrone
            setOptions(opt)
        } else {
            setOptions([])
        }
    }, [allSd, aree, inputValue])
    // Attenzione: useMemo è indispensabile qui perché altrimenti la funzione verrebbe ricalcolata ad ogni refresh
    const debouncedHandleChange = useMemo(
        () => {
            const handleChange = (ev: React.SyntheticEvent<Element, Event>, val: string) => setInputValue(val)
            return debounce(handleChange, 200)
        }, [setInputValue]);
    const plh = placeholer ?? 'Cerca applicazione ...'
    const handleExecute = (sd: ServiceDescriptor) => {
        sd && startApplication(sd)
        value && setValue(null)
    }
    const handleAutocompleteChange = (event: React.SyntheticEvent<Element, Event>, val: SearchOptionType | null, reason: AutocompleteChangeReason) => {
        // console.log(`%chandleAutocompleteChange: ${value?.label}, reason: ${reason}`, logConstants.magenta10ptBold)
        // Controlliamo se l'oggetto scelto è un ServiceDescriptor unico, nel qual caso lo attiviamo
        if (val?.group === 'sd') {
            if (val.items.length === 1) {
                handleExecute(val.items[0])
            } else {
                setValue(val)
            }
        } else {
            // Cerchiamo tutte le news trovate e ne facciamo una array
            var allNews = options.filter(o => o.group === 'news').map(o => o.news!)
            // console.log(`%csetNewsViewState: ${val?.news?.title}`, logConstants.red10ptBold)
            setNews(val?.news!)
            setNewsViewState({
                open: true,
                index: allNews.findIndex(n => n === val?.news!),
                items: allNews
            })
        }
    }
    return (
        <Box sx={{ alignSelf: 'center', width: '90%', mb: 1, mt: 1, px: 2, pt: 1 }}>
            <Paper id="search-form" elevation={2} component="form" sx={{ mb: 1, padding: '0px 0px', display: 'flex', alignItems: 'center', width: searchWidth, borderRadius: '24px', pl: 1 }}>
                <Autocomplete
                    id="search-autocomplete"
                    sx={{ width: "100%" }}
                    options={options}
                    value={value}
                    onChange={handleAutocompleteChange}
                    onInputChange={debouncedHandleChange}
                    autoHighlight
                    clearOnBlur={false}
                    autoSelect={false}
                    groupBy={x => x.group}
                    clearOnEscape
                    disableCloseOnSelect={false}
                    getOptionLabel={(option) => `${option.label}`}
                    filterOptions={(x) => x}
                    renderGroup={gruppo => (
                        <Box key={`${gruppo.group}-${gruppo.key}`}>
                            <Box sx={{ bgcolor: '#f0f0f0', px: 1, border: 'solid 1px #ddd' }}>
                                <Typography variant="h6">{gruppo.group === 'sd' ? t('Applicazioni') : t('News/Note operative')}</Typography>
                            </Box>
                            <Box>
                                {gruppo.children}
                            </Box>
                        </Box>)}
                    renderOption={(props, option) => (
                        <Box
                            component="li"
                            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                            {...props}
                            key={option.key}
                        >
                            <GroupedOptionViewer option={option} onExecute={handleExecute} />
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField sx={{ mx: 1 }}
                            {...params}
                            placeholder={plh}
                            variant='standard'
                            inputProps={{
                                ...params.inputProps,
                                autoComplete: "new-password" // disable autocomplete and autofill
                            }}
                        />
                    )}
                />
                {/* <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
                    <Search />
                </IconButton> */}
                <Button type="submit" variant="contained" size="small" color="primary"
                    sx={{shapeRendering: 'round', px: 2, py: 0.5, m: 0.5, marginLeft: '8px', borderRadius: '15px' }}>
                    Cerca
                </Button>
                {!userSetting?.carouselDisabled && !isOpen && <BannerSelector />}
            </Paper>
            {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: searchWidth, mb: 1 }}>
                <Typography variant='body2'>Service Descriptor Groups: {options?.length}</Typography>
            </Box> */}
            {value && <ResultViewerWithTable value={value} />}
            <NewsViewDialog
                open={newsViewState.open}
                listNews={newsViewState.items}
                index={newsViewState.index}
                onHandleCloseViewNews={handleAnnullaNewsView}
                atom={newsAtom}
            />
        </Box>
    )
}

export default SearchWithNewsSection

const GroupedOptionViewer = ({ option, onExecute }: { option: SearchOptionType, onExecute?: (sd: ServiceDescriptor) => void }) => {
    const preferitoExists = usePreferitoForSdExists()
    const editPreferito = useEditPreferito()
    const handlePrefOnClick = (sd: ServiceDescriptor) => (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        // console.log(`Imposta preferito per ${sd.id} => ${sd.urlDescription}`)

        editPreferito(sd)
    }
    const handleEsegui = (ev: React.MouseEvent<HTMLButtonElement>) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (onExecute && option.items.length === 1) {
            onExecute(option.items[0])
        }
    }
    const handleShowNews = (option: SearchOptionType) => () => console.log(`Should show  ${option.news?.id}, title: ${option.news?.title}`)
    const showYellowStar = (sdg: SearchOptionType) => sdg.items.reduce((p, c) => p || !!(preferitoExists(c.id)), false)
    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                "& > img": { mr: 2, flexShrink: 0 }
            }}
        >
            {option.group === 'sd'
                ? <>
                    {option.label}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100px"
                        }}
                    >
                        <Box onClick={handlePrefOnClick(option.items[0])}>
                            <StarFill size={20} color={showYellowStar(option) ? prefStarColor : notPrefStarColor} />
                        </Box>
                        <Button disabled={option.items.length > 1} onClick={handleEsegui}>Esegui</Button>
                    </Box>
                </>
                : <>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {option.news?.expired && <AccessTimeIcon fontSize='small' titleAccess="News scaduta" />}
                        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>{option.news?.title}</Typography>
                        <Typography variant='body1'>{option.news?.subtitle}</Typography>
                        <Typography variant='body2' sx={{ fontStyle: 'italic' }}>{excerpts(option.news?.body ?? '', { words: 20 })}</Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100px",
                            flexShrink: 0
                        }}
                    >
                        <Box onClick={handleShowNews(option)}>
                            {option.news?.notaOperativa === true ?
                            <OperationalNotesIcon color='action' />                            
                            :
                            <NewspaperIcon color='action' />
                            }
                        </Box>
                        <Button disabled={option.items.length > 1} onClick={handleShowNews(option)}>Vedi</Button>
                    </Box>
                </>
            }
        </Box>
    )
}